import {
  DC_Domain_Models_P4_BillBreakdown_BreakdownPeriod,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetPeriodicEnergyBillBreakdown = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  year: number;
  interval: DC_Domain_Models_P4_BillBreakdown_BreakdownPeriod;
};
/**
 * Gets the P4 periodic energy bill breakdown for a customer's account.
 *
 * @returns ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel If process went ok, body contains the periodic energy bill breakdown.
 */
export function getPeriodicEnergyBillBreakdown(
  { businessUnit, label, customerId, accountId, year, interval }: GetPeriodicEnergyBillBreakdown,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/period/breakdown`,
      query: { year, interval },
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'If the document cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetEnergyBillBreakdown = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  dateFrom: string;
  dateTo?: string;
  period?: DC_Domain_Models_P4_BillBreakdown_BreakdownPeriod;
};
/**
 * Gets the P4 energy bill breakdown for a customer's account.
 *
 * @returns ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel If process went ok, body contains the periodic energy bill breakdown.
 */
export function getEnergyBillBreakdown(
  { businessUnit, label, customerId, accountId, dateFrom, dateTo, period }: GetEnergyBillBreakdown,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/breakdown`,
      query: { dateFrom, dateTo, period },
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'If the document cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetEnergyBillBreakdownForAddress = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  addressId: string;
  dateFrom: string;
  dateTo?: string;
  period?: DC_Domain_Models_P4_BillBreakdown_BreakdownPeriod;
};
/**
 * Gets the P4 energy bill breakdown for a customer's account.
 *
 * @returns ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel If process went ok, body contains the periodic energy bill breakdown.
 */
export function getEnergyBillBreakdownForAddress(
  { businessUnit, label, customerId, accountId, addressId, dateFrom, dateTo, period }: GetEnergyBillBreakdownForAddress,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_EnergyBillBreakdown_EnergyBillBreakdownResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/address/${addressId}/breakdown`,
      query: { dateFrom, dateTo, period },
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'If the document cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}
